import React, { useReducer, useEffect, useState, useCallback, startTransition } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiClient } from '../../service/API';
import useTicketsTableData from '../../hooks/useTickets';
import FilterForm from './FilterForm';
import TicketTable from './TicketTable';
import PaginationControls from './PaginationControls';
import NotificationModal from '../TicketComponents/NotificationModal';
import { useAuth } from '../../auth/AuthHandler';
import { useWebSocketContext } from '../WebSocket/WebSocketContext';
import '../../styling/TicketList.css';

// NEW: store helpers
import {
  replacePage,
  upsertTicket,
  removeTicket,
  addTicketIdIfMissing,
} from '../../state/ticketsStore';

// Reducer unchanged
const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_PAGE_SIZE':
      return { ...state, pageSize: action.payload, currentPage: 0 };
    case 'SET_FILTERS':
      return { ...state, filters: action.payload, currentPage: 0 };
    case 'SET_FILTERS_ONLY':
      return { ...state, filters: action.payload };
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload };
    case 'RESET_FILTERS':
      return {
        ...state,
        filters: { ticketId: '', createdBy: '', assignTo: '', status: '', sysFreightRef: '', stage: '', sortBy: 'ticketId', sortDirection: 'ASC', service: '', startDate: '', endDate: '', listCategory: ''},
        pageSize: 10,
        currentPage: 0
      };
    default:
      return state;
  }
};

const TicketList = () => {
  const apiClient = useApiClient();
  const { stage } = useParams();
  const { authenticated, userEmail, userRoles } = useAuth();
  const [executeFilter, setExecuteFilter] = useState(true);

  const initializeState = () => {
    const savedState = sessionStorage.getItem('ticketListState');
    const defaultState = {
      filters: {
        ticketId: '',
        createdBy: '',
        assignTo: userRoles.includes('BOUSER') ? userEmail : '',
        status: '',
        sysFreightRef: '',
        stage: stage,
        service: '',
        sortBy: 'ticketId',
        sortDirection: 'ASC',
        startDate: '',
        endDate: '',
        listCategory: userRoles.includes('BOUSER') ? 'My Tickets' : 'All Tickets',
      },
      pageSize: 10,
      currentPage: 0,
    };
    return savedState ? JSON.parse(savedState) : defaultState;
  };

  const [state, dispatch] = useReducer(reducer, initializeState());

  const { isConnected, latestTicketEvents, mainRefresh, setMainRefresh } = useWebSocketContext();

  // Pull tickets with your existing hook (server paging/filtering)
  const { tickets, loading, refreshing, totalPages, error } = useTicketsTableData(
    state.filters,
    state.pageSize,
    state.currentPage,
    setMainRefresh,
    executeFilter,
    setExecuteFilter
  );

  const navigate = useNavigate();
  const [notificationMessage, setNotificationMessage] = useState('');
  const [tempFilters, setTempFilters] = useState(state.filters);

  // Keep the store in sync with the latest page
  useEffect(() => {
  if (!tickets) return;                 // don't clear on undefined
    // optionally: if (loading || refreshing) return;
    replacePage(tickets);
  }, [tickets]);

  // Handle stage param -> filters
  useEffect(() => {
    let stageState = stage;
    if (stage?.includes('All')) stageState = '';
    const newFilters = { ...state.filters, stage: stageState };
    dispatch({ type: 'SET_FILTERS_ONLY', payload: newFilters });
    setTempFilters(newFilters);
    updateSessionStorage(newFilters, state.pageSize, state.currentPage);
    setExecuteFilter(true);
  }, [stage]);

  const restoreState = () => {
    const savedState = sessionStorage.getItem('ticketListState');
    if (savedState) {
      const parsed = JSON.parse(savedState);
      dispatch({ type: 'SET_FILTERS', payload: parsed.filters });
      dispatch({ type: 'SET_PAGE_SIZE', payload: parsed.pageSize });
      dispatch({ type: 'SET_CURRENT_PAGE', payload: parsed.currentPage });
      setTempFilters(parsed.filters);
      setExecuteFilter(true);
    }
  };

  useEffect(() => {
    const handleNavigation = () => {
      if (document.visibilityState === 'visible') restoreState();
    };
    window.addEventListener('popstate', handleNavigation);
    return () => window.removeEventListener('popstate', handleNavigation);
  }, []);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) restoreState();
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') restoreState();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (isConnected) startTransition(() => setExecuteFilter(true));
  }, [isConnected]);

  useEffect(() => {
    startTransition(() => setExecuteFilter(true));
  }, [mainRefresh]);

  const updateSessionStorage = useCallback((filters, pageSize, currentPage) => {
    const stateToStore = { filters, pageSize, currentPage };
    sessionStorage.setItem('ticketListState', JSON.stringify(stateToStore));
  }, []);

  const handleFilterChange = (event) => {
    let fieldName, fieldValue;
    if (event?.target?.name) {
      const { name, value } = event.target;
      fieldName = name; fieldValue = value;
    } else {
      const { name, value } = event;
      fieldName = name; fieldValue = value;
    }
    setTempFilters({ ...tempFilters, [fieldName]: fieldValue });
  };

  const handlePageSizeChange = (size) => {
    dispatch({ type: 'SET_PAGE_SIZE', payload: size });
    updateSessionStorage(state.filters, size, state.currentPage);
  };

  const handleResetFilters = (e) => {
    e.preventDefault();
    clearTimeout(window.__tl_debounce);
    const base = {
      ticketId: '',
      createdBy: '',
      assignTo: '',
      status: '',
      sysFreightRef: '',
      stage: stage,
      service: '',
      startDate: '',
      endDate: '',
      sortBy: 'ticketId',
      sortDirection: 'ASC',
      listCategory: tempFilters.listCategory,
    };
    if (userRoles.includes('FOUSER')) {
      base.createdBy = tempFilters.listCategory !== 'All Tickets' ? userEmail : 'All';
    } else if (userRoles.includes('ADMIN')) {
      base.assignTo = tempFilters.listCategory !== 'All Tickets' ? userEmail : '';
    } else if (userRoles.includes('BOUSER') && !userRoles.includes('TEAMLEAD')) {
      base.assignTo = userEmail;
    } else if (userRoles.includes('BOUSER') && userRoles.includes('TEAMLEAD')) {
      base.assignTo = tempFilters.listCategory !== 'All Tickets' ? userEmail : '';
    }
    setTempFilters(base);
    dispatch({ type: 'SET_FILTERS', payload: base });
    updateSessionStorage(base, 10, 0);
    setExecuteFilter(true);
  };

  const applyFilter = () => {
    clearTimeout(window.__tl_debounce);
    dispatch({ type: 'SET_FILTERS', payload: tempFilters });
    updateSessionStorage(tempFilters, state.pageSize, state.currentPage);
    setExecuteFilter(true);
  };

  useEffect(() => {
    return () => clearTimeout(window.__tl_debounce);
  }, []);

  const handleFilterChangeAuto = (event) => {
    if (event === undefined) {
      setExecuteFilter(true);
      return;
    }
    const { name, value } = event.target;
    setTempFilters(prev => {
      const updated = { ...prev, [name]: value };
      clearTimeout(window.__tl_debounce);
      window.__tl_debounce = setTimeout(() => {
        dispatch({ type: 'SET_FILTERS', payload: updated });
        updateSessionStorage(updated, state.pageSize, state.currentPage);
        startTransition(() => setExecuteFilter(true));
      }, 120);
      return updated;
    });
  };

  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages) {
      dispatch({ type: 'SET_CURRENT_PAGE', payload: page });
      updateSessionStorage(state.filters, state.pageSize, page);
      setExecuteFilter(true);
    }
  };

  const handleCERButton = async (status, ticketId) => {
    try {
      console.log(ticketId);
      const response = await apiClient.patch(`/tickets/${status}/${ticketId}`);
      if (['REWORK', 'ERROR'].includes(status)) {
        const dataId = response.data.id;
        navigate(`/update-ticket/${dataId}`);
      } else {
        // Optimistic: fetch the single ticket and upsert
        const fresh = await apiClient.get(`/tickets/${ticketId}`);
        upsertTicket(fresh.data);
        setNotificationMessage(`Ticket ${ticketId} updated successful to ${status}!`);
      }
    } catch (error) {
      console.error('Error during approval:', error);
    }
  };

  useEffect(() => {
    if (error) setNotificationMessage(error);
  }, [error]);

  const handleToggleClick = (checked) => {
    const updatedFilters = { ...state.filters, listCategory: checked };

    if (['FOUSER', 'FOMANAGER', 'ADMIN'].some(role => userRoles.includes(role))) {
      updatedFilters.createdBy = checked !== 'All Tickets' ? userEmail : '';
    }
    if ((userRoles.includes('BOUSER') && userRoles.includes('TEAMLEAD'))) {
      updatedFilters.assignTo = checked !== 'All Tickets' ? userEmail : '';
    }

    dispatch({ type: 'SET_FILTERS', payload: updatedFilters });
    setTempFilters(updatedFilters);
    updateSessionStorage(updatedFilters, state.pageSize, state.currentPage);
    setExecuteFilter(true);
  };

  const onReverseSort = (name) => {
    const newSortDirection = state.filters.sortDirection === 'ASC' ? 'DESC' : 'ASC';
    dispatch({ type: 'SET_FILTERS', payload: { ...state.filters, sortBy: name, sortDirection: newSortDirection } });
    setExecuteFilter(true);
  };

  // --- MATCH helper so we can drop tickets that no longer pass filters ---
  const matchesFilters = useCallback((t) => {
    const F = state.filters;
    const v = (x) => (x ?? '').toString().toLowerCase();
    if (F.ticketId && !v(t.id).includes(v(F.ticketId))) return false;
    if (F.sysFreightRef && !v(t.sysFreightRef).includes(v(F.sysFreightRef))) return false;
    if (F.service && v(t.serviceType) !== v(F.service)) return false;
    if (F.stage && v(t.stage) !== v(F.stage)) return false;
    if (F.status && v(t.status) !== v(F.status)) return false;
    if (F.assignTo && v(t.assignedTo) !== v(F.assignTo)) return false;
    if (F.createdBy && v(t.createdBy) !== v(F.createdBy) && v(F.createdBy) !== 'all') return false;
    // Add date window checks if your server isn't already applying them.
    return true;
  }, [state.filters]);

  // --- WS: patch only the affected ticket (no table refresh) ---
  useEffect(() => {
    if (!latestTicketEvents || latestTicketEvents.type !== 'REFRESH') return;
    const id = latestTicketEvents.ticketId ?? latestTicketEvents.id;
    if (!id) return;

    // If WS already contains the ticket fields:
    if (latestTicketEvents.ticket && latestTicketEvents.ticket.id) {
      if (matchesFilters(latestTicketEvents.ticket)) {
        upsertTicket(latestTicketEvents.ticket);
        addTicketIdIfMissing(latestTicketEvents.ticket.id);
      }
      return;
    }

    // Otherwise, fetch the single ticket and upsert/remove based on filters
    let ignore = false;
    (async () => {
      try {
        const res = await apiClient.get(`/tickets/${id}`);
        if (ignore) return;
        const fresh = res.data;
        if (matchesFilters(fresh)) {
          upsertTicket(fresh);
          addTicketIdIfMissing(fresh.id);
        }
      } catch (e) {
        // If single read fails, you can optionally do a soft full refresh:
        // startTransition(() => setExecuteFilter(true));
      }
    })();
    return () => { ignore = true; };
  }, [latestTicketEvents, apiClient, matchesFilters]);

  return (
    <div className='ticket-list'>
      <div className="toggle-buttons">
        {['BOMANAGER','FOMANAGER', 'FOUSER', 'TEAMLEAD', 'ADMIN'].some(role => userRoles.includes(role)) && (
          <button
            className={`toggle-button ${tempFilters.listCategory === 'All Tickets' ? 'active' : ''}`}
            onClick={() => handleToggleClick('All Tickets')}
          >
            All Tickets
          </button>
        )}
        {['BOUSER','ADMIN'].some(role => userRoles.includes(role)) && (
          <button
            className={`toggle-button ${tempFilters.listCategory === 'Backup Tickets' ? 'active' : ''}`}
            onClick={() => handleToggleClick('Backup Tickets')}
          >
            Backup Tickets
          </button>
        )}
        <button
          className={`toggle-button ${tempFilters.listCategory === 'My Tickets' ? 'active' : ''}`}
          onClick={() => handleToggleClick('My Tickets')}
        >
          My Tickets
        </button>
      </div>

      <div className='filter-form-container'>
        <FilterForm
          notShowCreatedByFilter={(tempFilters.listCategory !== 'My Tickets')}
          filters={tempFilters}
          pageSize={state.pageSize}
          onFilterChange={handleFilterChange}
          onPageSizeChange={handlePageSizeChange}
          onResetFilters={handleResetFilters}
          onApplyFilters={applyFilter}
        />
      </div>

      <TicketTable
        loading={loading}
        refreshing={refreshing}
        filters={state.filters}
        tempFilter={tempFilters}
        onCERButton={handleCERButton}
        onReverseSort={onReverseSort}
        onApplyFilters={handleFilterChangeAuto}
      />

      <PaginationControls
        currentPage={state.currentPage}
        totalPages={totalPages}
        onPreviousPage={() => handlePageChange(state.currentPage - 1)}
        onNextPage={() => handlePageChange(state.currentPage + 1)}
      />

      <NotificationModal
        show={Boolean(notificationMessage)}
        message={notificationMessage}
        onClose={() => setNotificationMessage('')}
      />
    </div>
  );
};

export default TicketList;
